import { useEffect,useState } from "react";
import { Box,Button,Dialog,DialogActions,DialogContent,DialogTitle,Switch,TextField,Typography } from "@mui/material";
import { useAppTheme } from "../../../context/ThemeContext";
import { useAlert } from "../../../context/AlertContext";

const getCookie=(name)=>{
  const value=document.cookie.split("; ").find((row)=>row.startsWith(`${name}=`));
  return value?decodeURIComponent(value.split("=")[1]):"";
};

const readResponse=async(response)=>{
  const contentType=response.headers.get("content-type")||"";
  if(contentType.includes("application/json")) return response.json();
  if(!response.ok) throw new Error("El servidor no respondió correctamente.");
  return {};
};

const emptyForm={
  name:"",
  season:"",
  starts_on:"",
  ends_on:"",
  active:true
};

export default function TournamentForm({open,tournament,onClose,onSaved}){
  const {colors,typography,shape}=useAppTheme();
  const {showError}=useAlert();

  const [form,setForm]=useState(emptyForm);
  const [saving,setSaving]=useState(false);

  const isEditing=Boolean(tournament?.slug);

  useEffect(()=>{
    if(!open) return;

    if(tournament){
      setForm({
        name:tournament.name||"",
        season:tournament.season||"",
        starts_on:tournament.starts_on||"",
        ends_on:tournament.ends_on||"",
        active:tournament.active!==false
      });
    }else{
      setForm(emptyForm);
    }
  },[open,tournament]);

  const handleChange=(event)=>{
    const {name,value}=event.target;

    setForm((current)=>({
      ...current,
      [name]:value
    }));
  };

  const handleClose=()=>{
    if(saving) return;
    onClose();
  };

  const handleSave=async()=>{
    const name=form.name.trim();
    const season=form.season.trim();

    if(!name){
      showError("El nombre del torneo es obligatorio.");
      return;
    }

    if(
      form.starts_on&&
      form.ends_on&&
      form.ends_on<form.starts_on
    ){
      showError("La fecha de finalización no puede ser anterior a la fecha de inicio.");
      return;
    }

    try{
      setSaving(true);

      const url=isEditing?
        `/tournaments/${tournament.slug}/`:
        "/tournaments/";

      const response=await fetch(url,{
        method:isEditing?"PATCH":"POST",
        credentials:"include",
        headers:{
          "Content-Type":"application/json",
          "X-CSRFToken":getCookie("csrftoken")
        },
        body:JSON.stringify({
          name,
          season,
          starts_on:form.starts_on||null,
          ends_on:form.ends_on||null,
          active:form.active
        })
      });

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(
          data.error||
          data.detail||
          "No se pudo guardar el torneo."
        );
      }

      onSaved(data,isEditing);
    }catch(error){
      showError(
        error.message||
        "No se pudo guardar el torneo."
      );
    }finally{
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      disableScrollLock
      PaperProps={{
        sx:{
          borderRadius:shape.radiusLarge,
          boxShadow:"none",
          border:`1px solid ${colors.border}`,
          backgroundColor:colors.surface,
          backgroundImage:"none"
        }
      }}
    >
      <DialogTitle>
        <Typography
          sx={{
            fontSize:typography.sizes.xl,
            fontWeight:700,
            color:colors.text
          }}
        >
          {isEditing?"Editar torneo":"Nuevo torneo"}
        </Typography>

        <Typography
          sx={{
            mt:0.3,
            fontSize:typography.sizes.sm,
            color:colors.textSecondary
          }}
        >
          {isEditing?
            "Actualiza la información general del torneo.":
            "Ingresa la información general del nuevo torneo."
          }
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            pt:1,
            display:"flex",
            flexDirection:"column",
            gap:1.5
          }}
        >
          <TextField
            size="small"
            name="name"
            label="Nombre del torneo"
            value={form.name}
            onChange={handleChange}
            fullWidth
            required
            autoFocus
          />

          <TextField
            size="small"
            name="season"
            label="Temporada"
            value={form.season}
            onChange={handleChange}
            fullWidth
            placeholder="Ej. 2026"
          />

          <Box
            sx={{
              display:"grid",
              gridTemplateColumns:{xs:"1fr",sm:"1fr 1fr"},
              gap:1.5
            }}
          >
            <TextField
              size="small"
              name="starts_on"
              label="Fecha de inicio"
              type="date"
              value={form.starts_on}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{
                shrink:true
              }}
            />

            <TextField
              size="small"
              name="ends_on"
              label="Fecha de finalización"
              type="date"
              value={form.ends_on}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{
                shrink:true
              }}
            />
          </Box>

          <Box
            sx={{
              display:"flex",
              alignItems:"center",
              justifyContent:"space-between",
              gap:2,
              px:1.5,
              py:0.8,
              border:`1px solid ${colors.border}`,
              borderRadius:shape.radius
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize:typography.sizes.md,
                  fontWeight:600,
                  color:colors.text
                }}
              >
                Torneo activo
              </Typography>

              <Typography
                sx={{
                  mt:0.2,
                  fontSize:typography.sizes.sm,
                  color:colors.textSecondary
                }}
              >
                Permite utilizar este torneo en la administración.
              </Typography>
            </Box>

            <Switch
              size="small"
              checked={form.active}
              onChange={(event)=>
                setForm((current)=>({
                  ...current,
                  active:event.target.checked
                }))
              }
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px:3,
          py:2,
          borderTop:`1px solid ${colors.border}`
        }}
      >
        <Button
          size="small"
          onClick={handleClose}
          disabled={saving}
          sx={{
            color:colors.buttonText,
            textTransform:"none"
          }}
        >
          Cancelar
        </Button>

        <Button
          size="small"
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{
            px:2.2,
            textTransform:"none",
            backgroundColor:colors.secondary,
            "&:hover":{
              backgroundColor:colors.secondaryHover
            }
          }}
        >
          {saving?"Guardando...":isEditing?"Guardar cambios":"Crear torneo"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}